import * as React from "react";
import { useEffect, useState } from "react";
import axios from "axios";
import {
    Alert,
    Box,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Collapse,
    Divider,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import AssessmentIcon from "@mui/icons-material/Assessment";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

interface RoiBlob {
    name: string;
    clientName: string;
    createdOn: string;
    metadata: Record<string, string>;
}

interface RepGroup {
    repEmail: string;
    repName: string;
    rois: RoiBlob[];
}

const PAR_BLUE = "#003F81";
const PAR_BLUE_LIGHT = "#e8eef6";
const GROUP_COLOR = "#2c5f9e";
const GROUP_COLOR_END = "#1e4d8c";

const SOLUTION_COLORS: Record<string, "primary" | "secondary" | "success" | "warning" | "error" | "info" | "default"> = {
    "PAR Scales": "primary",
    "PAR 360 Plus": "info",
    "PAR 360 Premium": "info",
    "Electronic Shelf Labels (ESLs)": "success",
    "PAR SameDay": "warning",
    "TrackCore IR": "secondary",
    "TrackCore OR": "error",
};

function fmt(val: string | undefined, prefix = "", suffix = "") {
    if (!val || val === "0" || val === "") return null;
    const num = parseFloat(val);
    if (isNaN(num)) return null;
    return `${prefix}${num.toLocaleString("en-US", { maximumFractionDigits: 0 })}${suffix}`;
}

function extractRepEmail(blobName: string): string {
    const filename = blobName.split("/").pop() ?? blobName;
    const withoutExt = filename.replace(/\.json$/i, "");
    const match = withoutExt.match(/^(.+)_[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i);
    return match ? match[1] : withoutExt;
}

function groupByRep(blobs: RoiBlob[]): RepGroup[] {
    const map = new Map<string, RepGroup>();
    for (const blob of blobs) {
        const email = blob.metadata?.repEmail || extractRepEmail(blob.name);
        const name = blob.metadata?.repName || email;
        if (!map.has(email)) {
            map.set(email, { repEmail: email, repName: name, rois: [] });
        }
        map.get(email)!.rois.push(blob);
    }
    return Array.from(map.values()).sort((a, b) => a.repName.localeCompare(b.repName));
}

function StatCard({ icon, label, value }: { icon: React.ReactNode; label: string; value: string | number }) {
    return (
        <Card elevation={0} sx={{ border: `1px solid ${PAR_BLUE_LIGHT}`, borderRadius: 2, minWidth: 150, flex: 1 }}>
            <CardContent sx={{ display: "flex", alignItems: "center", gap: 1.5, py: "12px !important" }}>
                <Box sx={{ color: PAR_BLUE, display: "flex" }}>{icon}</Box>
                <Box>
                    <Typography variant="h5" fontWeight={700} color={PAR_BLUE} lineHeight={1}>{value}</Typography>
                    <Typography variant="caption" color="text.secondary">{label}</Typography>
                </Box>
            </CardContent>
        </Card>
    );
}

function DataCell({ value, align = "right" }: { value: string | null; align?: "left" | "right" }) {
    return (
        <TableCell align={align}>
            {value
                ? <Typography variant="body2" fontWeight={500}>{value}</Typography>
                : <Typography variant="body2" color="text.disabled" sx={{ fontStyle: "italic", fontSize: "0.7rem" }}>not available</Typography>
            }
        </TableCell>
    );
}

function RepRow({ group }: { group: RepGroup }) {
    const [open, setOpen] = useState(true);
    const repLabel = group.repName !== group.repEmail
        ? group.repName
        : group.repEmail;

    return (
        <>
            <TableRow
                sx={{
                    background: `linear-gradient(90deg, ${GROUP_COLOR} 0%, ${GROUP_COLOR_END} 100%)`,
                    cursor: "pointer",
                    "&:hover": { filter: "brightness(1.08)" },
                }}
                onClick={() => setOpen(!open)}
            >
                <TableCell sx={{ color: "white", fontWeight: 700, py: 1.25, borderBottom: "none" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <IconButton size="small" sx={{ color: "white", p: 0.25 }}>
                            {open ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
                        </IconButton>
                        <Box>
                            <Typography variant="body2" fontWeight={700} color="white" lineHeight={1.2}>
                                {repLabel}
                            </Typography>
                            {group.repName !== group.repEmail && (
                                <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.65)" }}>
                                    {group.repEmail}
                                </Typography>
                            )}
                        </Box>
                    </Box>
                </TableCell>
                <TableCell sx={{ color: "rgba(255,255,255,0.8)", borderBottom: "none" }} colSpan={6} align="right">
                    <Chip
                        label={`${group.rois.length} ROI${group.rois.length !== 1 ? "s" : ""}`}
                        size="small"
                        sx={{ backgroundColor: "rgba(255,255,255,0.2)", color: "white", fontWeight: 600, fontSize: "0.72rem" }}
                    />
                </TableCell>
            </TableRow>
            <TableRow sx={{ backgroundColor: "transparent" }}>
                <TableCell colSpan={7} sx={{ p: 0, border: 0 }}>
                    <Collapse in={open} timeout="auto" unmountOnExit>
                        <Table size="small">
                            <TableBody>
                                {group.rois.map((roi, idx) => {
                                    const solutions = (roi.metadata?.solutions ?? "").split(",").map(s => s.trim()).filter(Boolean);
                                    return (
                                        <TableRow
                                            key={roi.name}
                                            hover
                                            sx={{
                                                backgroundColor: idx % 2 === 0 ? "white" : "#fafbfc",
                                                "&:hover": { backgroundColor: PAR_BLUE_LIGHT },
                                            }}
                                        >
                                            <TableCell sx={{ pl: 5, width: "22%", borderBottom: `1px solid #f0f0f0` }}>
                                                <Typography variant="body2" fontWeight={500}>
                                                    {roi.clientName || <span style={{ color: "#aaa", fontStyle: "italic" }}>Unnamed</span>}
                                                </Typography>
                                            </TableCell>
                                            <TableCell sx={{ width: "13%", borderBottom: `1px solid #f0f0f0` }}>
                                                <Typography variant="body2" color="text.secondary">
                                                    {roi.createdOn
                                                        ? new Date(roi.createdOn).toLocaleDateString("en-US", {
                                                            month: "short", day: "numeric", year: "numeric",
                                                        })
                                                        : "—"}
                                                </Typography>
                                            </TableCell>
                                            <TableCell sx={{ width: "27%", borderBottom: `1px solid #f0f0f0` }}>
                                                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                                                    {solutions.length > 0
                                                        ? solutions.map(s => (
                                                            <Chip
                                                                key={s}
                                                                label={s}
                                                                size="small"
                                                                color={SOLUTION_COLORS[s] ?? "default"}
                                                                variant="outlined"
                                                                sx={{ fontSize: "0.68rem", height: 20 }}
                                                            />
                                                        ))
                                                        : <Typography variant="caption" sx={{ color: "#bbb", fontStyle: "italic" }}>not available</Typography>
                                                    }
                                                </Box>
                                            </TableCell>
                                            <DataCell value={fmt(roi.metadata?.startupFees, "$")} />
                                            <DataCell value={fmt(roi.metadata?.annualRecurring, "$")} />
                                            <DataCell value={fmt(roi.metadata?.roiYear1Pct, "", "%")} />
                                            <TableCell sx={{ width: "6%", borderBottom: `1px solid #f0f0f0` }} />
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </Collapse>
                </TableCell>
            </TableRow>
        </>
    );
}

export const ROIReportPage = () => {
    const [groups, setGroups] = useState<RepGroup[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        axios.get<RoiBlob[]>("api/Storage/GetAll")
            .then((res) => {
                setGroups(groupByRep(res.data ?? []));
                setLoading(false);
            })
            .catch((err) => {
                setError(err?.response?.data ?? "Failed to load ROI report.");
                setLoading(false);
            });
    }, []);

    if (loading) {
        return (
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", mt: 10, gap: 2 }}>
                <CircularProgress sx={{ color: PAR_BLUE }} />
                <Typography variant="body2" color="text.secondary">Loading ROI report…</Typography>
            </Box>
        );
    }

    if (error) {
        return <Box sx={{ mt: 2 }}><Alert severity="error">{error}</Alert></Box>;
    }

    const totalRois = groups.reduce((s, g) => s + g.rois.length, 0);
    const roiWithData = groups.reduce((s, g) => s + g.rois.filter(r => r.metadata?.solutions).length, 0);

    return (
        <Box sx={{ pt: 0 }}>
            {/* Header */}
            <Box sx={{ mb: 2 }}>
                <Typography variant="h4" fontWeight={800} color={PAR_BLUE} gutterBottom={false}>
                    ROI Report
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    Sales activity across all PAR Excellence ROI analyses
                </Typography>
            </Box>

            <Divider sx={{ mb: 2 }} />

            {/* Stat Cards */}
            <Box sx={{ display: "flex", gap: 2, mb: 2, flexWrap: "wrap" }}>
                <StatCard icon={<PeopleAltIcon />} label="Sales Reps" value={groups.length} />
                <StatCard icon={<AssessmentIcon />} label="Total ROIs" value={totalRois} />
                <StatCard icon={<AssessmentIcon />} label="ROIs with Full Data" value={roiWithData} />
            </Box>

            {/* Historical data notice */}
            {roiWithData < totalRois && (
                <Alert
                    icon={<InfoOutlinedIcon fontSize="small" />}
                    severity="info"
                    sx={{ mb: 2, fontSize: "0.8rem", py: 0.5 }}
                >
                    <strong>{totalRois - roiWithData} older ROI{totalRois - roiWithData !== 1 ? "s" : ""}</strong> show "not available" for PAR offerings, fees, and ROI % — these were saved before the reporting update. Only newly saved ROIs will include this data.
                </Alert>
            )}

            {groups.length === 0 ? (
                <Alert severity="info">No saved ROIs found.</Alert>
            ) : (
                <TableContainer component={Paper} elevation={2} sx={{ borderRadius: 2, overflow: "hidden" }}>
                    <Table size="small">
                        <TableHead>
                            <TableRow sx={{ backgroundColor: "#f0f4f9" }}>
                                <TableCell sx={{ fontWeight: 700, color: "#444", pl: 5, fontSize: "0.78rem", py: 1.25 }}>Client</TableCell>
                                <TableCell sx={{ fontWeight: 700, color: "#444", fontSize: "0.78rem" }}>Date Saved</TableCell>
                                <TableCell sx={{ fontWeight: 700, color: "#444", fontSize: "0.78rem" }}>PAR Offerings</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 700, color: "#444", fontSize: "0.78rem" }}>
                                    <Tooltip title="One-time implementation and onboarding fees" placement="top">
                                        <span style={{ cursor: "help", borderBottom: "1px dotted #888" }}>Start-up Fees</span>
                                    </Tooltip>
                                </TableCell>
                                <TableCell align="right" sx={{ fontWeight: 700, color: "#444", fontSize: "0.78rem" }}>
                                    <Tooltip title="Total annual recurring PAR subscription revenue" placement="top">
                                        <span style={{ cursor: "help", borderBottom: "1px dotted #888" }}>Annual ARR</span>
                                    </Tooltip>
                                </TableCell>
                                <TableCell align="right" sx={{ fontWeight: 700, color: "#444", fontSize: "0.78rem" }}>
                                    <Tooltip title="Year 1 return on investment percentage" placement="top">
                                        <span style={{ cursor: "help", borderBottom: "1px dotted #888" }}>Yr 1 ROI %</span>
                                    </Tooltip>
                                </TableCell>
                                <TableCell />
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {groups.map((group) => (
                                <RepRow key={group.repEmail} group={group} />
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            <Typography variant="caption" color="text.disabled" sx={{ mt: 1.5, display: "block" }}>
                ROIs are grouped by sales rep · Sorted alphabetically · Click a rep row to expand or collapse
            </Typography>
        </Box>
    );
};
